import { Box, useTheme } from "@mui/material";

/**
 * The landing site's Aceternity aurora, ported to sx for the native auth
 * screens — deep Insight emerald/teal bands drifting under a 10px blur,
 * `lighten`-blended so the field can never blow out to white. Pure CSS (no
 * WebGL, no rAF): one 60s background-position drift, frozen entirely under
 * prefers-reduced-motion. Masked to fade out toward the bottom so the action
 * zone underneath stays clean.
 *
 * Light mode gets a quiet emerald wash instead — the striped field is
 * authored against a dark floor and reads as smudges on white.
 */

const DARK_GRADIENT =
  "repeating-linear-gradient(100deg, #112723 0%, #112723 7%, transparent 10%, transparent 12%, #112723 16%)";
const AURORA_GRADIENT =
  "repeating-linear-gradient(100deg, #10b981 10%, #0d9488 15%, #34d399 20%, #059669 25%, #0f766e 30%)";

interface AuroraGlowProps {
  /** Reach the bottom edge (dimmed) instead of fading out two-thirds down —
      for screens whose content fills the full height, like the paywall. */
  fullHeight?: boolean;
}

export const AuroraGlow = ({ fullHeight = false }: AuroraGlowProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (!isDark) {
    return (
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(130% 80% at 50% -20%, rgba(34, 197, 94, 0.14), transparent 70%)",
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        background:
          "radial-gradient(130% 100% at 50% -20%, rgba(28, 58, 50, 0.4), transparent 72%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "-10px",
          opacity: 0.25,
          willChange: "transform",
          filter: "blur(10px)",
          backgroundImage: `${DARK_GRADIENT}, ${AURORA_GRADIENT}`,
          backgroundSize: "300%, 200%",
          backgroundPosition: "50% 50%, 50% 50%",
          maskImage: fullHeight
            ? "linear-gradient(to bottom, black 0%, black 55%, rgba(0,0,0,0.35) 100%)"
            : "linear-gradient(to bottom, black 0%, black 35%, transparent 90%)",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: `${DARK_GRADIENT}, ${AURORA_GRADIENT}`,
            backgroundSize: "200%, 100%",
            mixBlendMode: "lighten",
            animation: "authAurora 60s linear infinite",
          },
          "@keyframes authAurora": {
            from: { backgroundPosition: "50% 50%, 50% 50%" },
            to: { backgroundPosition: "350% 50%, 350% 50%" },
          },
          "@media (prefers-reduced-motion: reduce)": {
            "&::after": { animation: "none" },
          },
        }}
      />
    </Box>
  );
};
